
import { useState } from 'react';
import type { AllowedTransition } from '@/entities/ticket';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/shared/ui';
import { TransitionFields } from './TransitionFields';
import type { TemplateContext } from '@/features/tickets/ui/TemplatePanel';
import {
  collectTransitionInputs,
  FULL_ACCESS_SCOPE,
  hasInvalidValues,
  hasUnfilledRequired,
  type TransitionValues,
} from './transition-inputs';
import styles from '../TicketDetail.module.css';

export const TransitionDialog: React.FC<{
  open: boolean;
  transition: AllowedTransition | null;
  title: string;
  statusLabel: string;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (inputs: Record<string, unknown>) => void;
  templateContext?: TemplateContext | null;
}> = ({
  open,
  transition,
  title,
  statusLabel,
  loading,
  error,
  onClose,
  onConfirm,
  templateContext,
}) => {
  const [values, setValues] = useState<TransitionValues>({});
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [scope, setScope] = useState(FULL_ACCESS_SCOPE);
  const specs = transition?.inputs ?? [];

  const reset = () => {
    setValues({});
    setUserNames({});
    setScope(FULL_ACCESS_SCOPE);
  };

  const handleConfirm = () => {
    const inputs = collectTransitionInputs(specs, values, scope);
    onConfirm(inputs);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next || loading) return;
        reset();
        onClose();
      }}
    >
      <DialogContent>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          Заполните данные перехода — заявка сменит статус на «{statusLabel}».
        </DialogDescription>

        <div className={styles.transitionForm}>
          <TransitionFields
            specs={specs}
            values={values}
            onChange={(key, value) => setValues((v) => ({ ...v, [key]: value }))}
            userNames={userNames}
            onPickUser={(key, user) => {
              setValues((v) => ({ ...v, [key]: user.id }));
              setUserNames((v) => ({ ...v, [key]: user.displayName ?? user.id }));
            }}
            scope={scope}
            onScopeChange={setScope}
            templateContext={templateContext}
          />

          {error && <p className={styles.error}>{error}</p>}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={loading || hasUnfilledRequired(specs, values) || hasInvalidValues(specs, values)}
          >
            {loading ? 'Сохранение…' : 'Подтвердить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
